/**
 * Client-side image cropping for brand marks.
 *
 * `sharp` does the final crop on the server, but it only receives an image —
 * it has no idea where the org meant the mark to sit. Showing the user a
 * resizable frame over the uploaded file and shipping the frame's crop region
 * is what keeps "the logo's wordmark got chopped" from reaching a print run.
 */

export type BrandAspect = "square" | "landscape";

export interface AspectSpec {
  id: BrandAspect;
  ratio: number;
  label: string;
  /** Where this ratio ends up being drawn — the reason for choosing it. */
  hint: string;
}

export const SQUARE_ASPECT: AspectSpec = {
  id: "square",
  ratio: 1,
  label: "Square (1:1)",
  hint: "Header avatar, karigar band, PWA icons",
};

/** Square-only marks (favicons) get this instead — PWA icons are a circle. */
export const SQUARE_ONLY_HINT = "PWA icons are a circle, so only a square crop is kept.";

export const BRAND_ASPECTS: AspectSpec[] = [
  SQUARE_ASPECT,
  {
    id: "landscape",
    ratio: 16 / 9,
    label: "Landscape (16:9)",
    hint: "Poster header, sign-in panel",
  },
];

export const CROP_DEFAULT_RATIO = 1;
export const CROP_MIN_SOURCE_EDGE = 128;

/** The crop region as proportions of the source image, `0..1` on both axes. */
export interface CropRegion {
  x: number;
  y: number;
  w: number;
  h: number;
}

export function cropRegionDataUrl(
  image: HTMLImageElement,
  region: CropRegion,
): string {
  const sx = Math.max(0, Math.min(1, region.x));
  const sy = Math.max(0, Math.min(1, region.y));
  const sw = Math.max(1e-4, Math.min(1, region.w));
  const sh = Math.max(1e-4, Math.min(1, region.h));

  const sourceX = sx * image.naturalWidth;
  const sourceY = sy * image.naturalHeight;
  const sourceW = sw * image.naturalWidth;
  const sourceH = sh * image.naturalHeight;

  const targetW = Math.max(1, Math.round(sourceW * Math.min(1, 2048 / sourceW)));
  const targetH = Math.max(1, Math.round(sourceH * (targetW / sourceW)));

  const canvas = document.createElement("canvas");
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("This browser cannot crop that image.");
  ctx.drawImage(image, sourceX, sourceY, sourceW, sourceH, 0, 0, targetW, targetH);
  // Logos may contain transparent pixels. JPEG flattens those pixels to black,
  // so keep the crop lossless and transparent; the API converts opaque crops
  // to JPEG after inspecting the image metadata.
  return canvas.toDataURL("image/png");
}
